import { sameTerms, type TermsAcceptance } from "../terms/terms";

/**
 * ONE BOOKING ATTEMPT: one name for it, and one frozen request under that name.
 *
 * `booking-v1@1.4.0` makes `createBooking` recoverable, and the mechanism is
 * `CreateBookingInput.idempotencyKey`. The contract's own words: send the SAME
 * key and you get the SAME booking back; send a different key and you create a
 * second booking. So the key is not a retry token — it is the NAME of a logical
 * attempt, and every send belonging to that attempt must carry it unchanged
 * alongside byte-identical arguments.
 *
 * That makes the dangerous operation not "retrying" but "retrying with anything
 * different". Reusing a key with different arguments is refused
 * (`IDEMPOTENCY_CONFLICT`) rather than answered — which is the server being
 * careful, and which this module exists to never provoke.
 *
 * WHY THE INPUT IS FROZEN RATHER THAN REBUILT.
 *
 * The recovery path resends the request. If it rebuilt the input from current
 * component state, anything that drifted in between — a hold refreshed under
 * the countdown, say — would produce a different argument set under the same
 * key. The client would have turned a
 * recoverable outcome into a conflict it cannot interpret. So the input is
 * captured ONCE, deep-frozen, and resent verbatim.
 */

/**
 * What the caller believes it is booking, COPIED at attempt creation.
 *
 * Kept as its own frozen copy rather than the caller's object: the check
 * compares a replayed response against this, and a caller that mutated its
 * selection afterwards would silently change what "the same appointment" means
 * — turning the attempt's own booking into a mismatch and stranding a booking
 * that exists.
 */
export interface FrozenExpectation {
  readonly professionalId: string;
  readonly serviceId: string;
  readonly startTime: string;
}

/** The exact variables one attempt sends, every time it sends. */
export interface FrozenBookingInput {
  readonly holdId: string;
  readonly idempotencyKey: string;
  /**
   * The Terms the person accepted for THIS send, or absent when none were
   * published. The one field a send may change under the same key: Backend
   * leaves it out of the idempotency fingerprint (createBooking.ts,
   * bookingRequestFingerprint over holdId, guestContact and
   * guestContactVerificationId), and reads the replay receipt BEFORE it checks
   * the Terms. So a re-ask after TERMS_STALE re-sends under the same name, and
   * a booking that name already committed replays whatever the Terms say.
   * See `withTermsAcceptance`.
   */
  readonly termsAcceptance?: TermsAcceptance;
}

export interface BookingAttempt {
  /** Opaque. Never rendered, logged, or put in a URL — see `describeAttempt`. */
  readonly idempotencyKey: string;
  readonly input: FrozenBookingInput;
  /**
   * THE FIREBASE UID THIS ATTEMPT BELONGS TO.
   *
   * Booking receipts are scoped to the calling account: the contract says keys
   * are account-scoped, so the same key under a DIFFERENT uid does not name
   * this attempt at all. Replaying it would not recover the booking — it would
   * either create a second one under the new identity or be refused, and both
   * outcomes are worse than declining to ask.
   *
   * An anonymous session is not durable. A private-mode eviction, a cleared
   * store, or a refresh that fails to restore can hand the next call a NEW
   * anonymous uid, which is exactly when a client is most likely to be
   * recovering a booking. So the uid is bound here and re-checked before every
   * replay.
   */
  readonly uid: string;
  /** What the first send was told to expect. Copied, never the caller's object. */
  readonly expected: FrozenExpectation;
}

/**
 * 8–128 characters of letters, digits, hyphen or underscore — the contract's
 * stated shape. Asserted here as well as trusted, because a key that fails
 * server validation turns a recoverable booking into `INVALID_INPUT` at exactly
 * the moment recovery matters.
 */
const KEY_PATTERN = /^[A-Za-z0-9_-]{8,128}$/;

export function isContractValidKey(value: string): boolean {
  return KEY_PATTERN.test(value);
}

/**
 * A fresh opaque key.
 *
 * `crypto.randomUUID()` where available: 36 characters, hyphens kept, entirely
 * within the contract's alphabet, and the shape the contract names as ideal.
 * The fallback is `crypto.getRandomValues`, not `Math.random` — this value is
 * the only thing that lets a client re-reach a committed booking, and a
 * predictable one lets a different session name someone else's attempt. Keys
 * are account-scoped server-side, so this is defence in depth rather than the
 * only barrier, but a weak generator here would be a security choice made by
 * accident.
 *
 * There is deliberately no `Math.random` path. On a browser without WebCrypto
 * we throw — refusing to begin is correct when the thing we cannot build is the
 * ability to recover.
 *
 * This throw is PRE-DISPATCH and `useBooking.commit` unwinds it as such: the
 * send latch is cleared and the screen moves to `cannot-start`, which says only
 * that nothing was sent. The comment here previously asserted that the caller
 * "surfaces it as a failure to start" — it did not. The call sat outside the
 * try/finally, so the throw escaped, latched `sending` permanently, and became
 * an unhandled rejection. The claim is now true because the boundary exists,
 * not because the comment says so.
 */
export function generateIdempotencyKey(): string {
  const webCrypto = globalThis.crypto;
  if (webCrypto && typeof webCrypto.randomUUID === "function") {
    return webCrypto.randomUUID();
  }
  if (webCrypto && typeof webCrypto.getRandomValues === "function") {
    const bytes = new Uint8Array(16);
    webCrypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  throw new Error(
    "cannot start a booking attempt: this browser has no WebCrypto, so no idempotency key can " +
      "be generated. Beginning without one would make a lost response unrecoverable."
  );
}

/**
 * Begin a logical attempt. Called ONCE per attempt, before the first send.
 *
 * A new hold is a genuinely different attempt and must call this again — see
 * `isSameAttemptInput`. Everything else,
 * including every recovery check, reuses what this returned.
 */
export function beginBookingAttempt(
  holdId: string,
  /** The uid this attempt is dispatched as. Established BEFORE this is called. */
  uid: string,
  expected: FrozenExpectation
): BookingAttempt {
  if (!uid) {
    // Refusing to begin is correct when the thing we cannot establish is who
    // the booking would belong to. A key minted without an identity names an
    // attempt nobody can look up.
    throw new Error("cannot begin a booking attempt without an established identity");
  }
  const idempotencyKey = generateIdempotencyKey();
  if (!isContractValidKey(idempotencyKey)) {
    throw new Error("generated an idempotency key outside the contract's accepted shape");
  }
  const input: FrozenBookingInput = Object.freeze({
    holdId,
    idempotencyKey,
  });
  return Object.freeze({
    idempotencyKey,
    input,
    uid,
    // COPIED, not referenced. `{...expected}` is the whole point of this line.
    expected: Object.freeze({
      professionalId: expected.professionalId,
      serviceId: expected.serviceId,
      startTime: expected.startTime,
    }),
  });
}

/** May this attempt be replayed by the identity currently signed in? */
export function isSameIdentity(attempt: BookingAttempt, uid: string | null): boolean {
  return typeof uid === "string" && uid.length > 0 && uid === attempt.uid;
}

/**
 * Would this hold be the SAME attempt as one already begun?
 *
 * The hold is the only argument a signed-in client's booking fingerprints:
 * Backend takes the contact from the account, so there is none to compare.
 * (Anonymous bookings go through the verified guest checkout, which keeps
 * its own frozen submission: features/guestCheckout/checkout.ts.)
 */
export function isSameAttemptInput(attempt: BookingAttempt, holdId: string): boolean {
  return attempt.input.holdId === holdId;
}

/**
 * THE SAME ATTEMPT, CARRYING THESE TERMS. The key, the hold, the uid and the
 * expectation are the attempt's own and do not change.
 *
 * Why this is not a new attempt: `termsAcceptance` is outside Backend's
 * idempotency fingerprint, and Backend answers a committed key from its
 * receipt before it looks at the Terms. A NEW key after a Terms refusal would
 * be the one way to make a second booking out of a first send that did commit
 * after all (Web Director, rc.8: the re-ask KEEPS the key).
 *
 * Returns the attempt itself when the Terms are unchanged, so a plain resend
 * stays the identical frozen object.
 */
export function withTermsAcceptance(
  attempt: BookingAttempt,
  acceptance: TermsAcceptance | null
): BookingAttempt {
  if (sameTerms(attempt.input.termsAcceptance ?? null, acceptance)) return attempt;
  const { termsAcceptance: _previous, ...rest } = attempt.input;
  const input: FrozenBookingInput = Object.freeze({
    ...rest,
    ...(acceptance ? { termsAcceptance: acceptance } : {}),
  });
  return Object.freeze({ ...attempt, input });
}

/**
 * What may be said about an attempt OUTSIDE this module.
 *
 * The key names a booking that can be re-reached by anyone holding it. It does
 * not belong in a URL, a log line, an analytics
 * event, a diagnostic, an error message or a test artifact. This returns the
 * only safe summary — whether an attempt exists — so that code needing to
 * describe one has something true to say that is not the key.
 */
export function describeAttempt(attempt: BookingAttempt | null): string {
  return attempt ? "a booking attempt is in progress" : "no booking attempt";
}
